import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { confirmDialog } from '../components/ConfirmDialog';

type BackupItem = {
  id: string;
  filename: string;
  size?: string;
  size_human?: string;
  checksum?: string;
  engine?: string;
  compressed?: boolean;
  type?: string;
  s3?: {
    bucket?: string;
    key?: string;
    etag?: string | null;
    uploaded_at?: string;
  };
  created_at?: string;
  modified_at?: string;
};

type BackupConfig = {
  enabled: boolean;
  destination_path: string;
  retention_days: number;
  interval_minutes: number;
  cron_expression: string;
  use_cron: boolean;
  timezone: string;
  alert_user_id: string;
  notify_email: string;
  notify_phone: string;
  s3_enabled: boolean;
  s3_region: string;
  s3_bucket: string;
  s3_prefix: string;
  s3_access_key_id: string;
  s3_secret_access_key: string;
  s3_retention_days: number;
  last_run?: string | null;
  next_run?: string | null;
};

const defaultConfig: BackupConfig = {
  enabled: false,
  destination_path: 'server/backups',
  retention_days: 30,
  interval_minutes: 1440,
  cron_expression: '0 20 * * *',
  use_cron: true,
  timezone: 'America/Argentina/Buenos_Aires',
  alert_user_id: '',
  notify_email: '',
  notify_phone: '',
  s3_enabled: false,
  s3_region: '',
  s3_bucket: '',
  s3_prefix: '',
  s3_access_key_id: '',
  s3_secret_access_key: '',
  s3_retention_days: 90,
  last_run: null,
  next_run: null
};

const Backups: React.FC = () => {
  const { hasRole } = useAuth();
  const isAdmin = hasRole('admin');
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [backups, setBackups] = useState<BackupItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState<BackupConfig>(defaultConfig);
  const [users, setUsers] = useState<{ id: string; name: string; email?: string; phone?: string }[]>([]);
  const requestTimeoutMs = 8000;

  const fetchBackups = async () => {
    try {
      const res = await axios.get('/api/backup/list', { timeout: requestTimeoutMs });
      setBackups(res.data || []);
    } catch (error: any) {
      console.error('Error loading backups', error);
      toast.error(error.response?.data?.error || 'No se pudieron cargar los respaldos');
    }
  };

  const fetchConfig = async () => {
    try {
      const res = await axios.get('/api/backup/config', { timeout: requestTimeoutMs });
      const payload = res.data || {};
      setConfig({
        enabled: Boolean(payload.enabled),
        destination_path: payload.destination_path || '',
        retention_days: Number(payload.retention_days || 30),
        interval_minutes: Number(payload.interval_minutes || 1440),
        cron_expression: payload.cron_expression || '',
        use_cron: Boolean(payload.use_cron),
        timezone: payload.timezone || 'America/Argentina/Buenos_Aires',
        alert_user_id: payload.alert_user_id || '',
        notify_email: payload.notify_email || '',
        notify_phone: payload.notify_phone || '',
        s3_enabled: Boolean(payload.s3_enabled),
        s3_region: payload.s3_region || '',
        s3_bucket: payload.s3_bucket || '',
        s3_prefix: payload.s3_prefix || '',
        s3_access_key_id: payload.s3_access_key_id || '',
        s3_secret_access_key: payload.s3_secret_access_key || '',
        s3_retention_days: Number(payload.s3_retention_days || 90),
        last_run: payload.last_run || null,
        next_run: payload.next_run || null
      });
    } catch (error: any) {
      console.error('Error loading backup config', error);
      toast.error(error.response?.data?.error || 'No se pudo cargar la configuracion');
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await axios.get('/api/users', { timeout: requestTimeoutMs });
      const list = Array.isArray(res.data?.users) ? res.data.users : [];
      const mapped = list.map((u: any) => ({
        id: u.id,
        name: [u.firstName, u.lastName].filter(Boolean).join(' ') || u.username || u.email || u.id,
        email: u.email,
        phone: u.phone
      }));
      setUsers(mapped);
    } catch (error: any) {
      console.error('Error loading users', error);
      toast.error(error.response?.data?.error || 'No se pudieron cargar los usuarios');
    }
  };

  useEffect(() => {
    if (!isAdmin) {
      setLoading(false);
      return;
    }
    const load = async () => {
      setLoading(true);
      await Promise.allSettled([fetchBackups(), fetchConfig(), fetchUsers()]);
      setLoading(false);
    };
    load();
  }, [isAdmin]);

  const createManualBackup = async () => {
    try {
      await axios.post('/api/backup/create');
      toast.success('Copia creada correctamente');
      fetchBackups();
    } catch (error: any) {
      console.error('Error creating backup', error);
      toast.error(error.response?.data?.error || 'No se pudo crear la copia');
    }
  };

  const downloadBackup = (id: string) => {
    const token = localStorage.getItem('token');
    const url = token ? `/api/backup/download/${id}?token=${token}` : `/api/backup/download/${id}`;
    window.open(url, '_blank', 'noopener');
  };

  const restoreBackup = async (id: string) => {
    if (!await confirmDialog('Restaurar esta copia reemplazara la base actual. Continuar?')) return;
    try {
      await axios.post(`/api/backup/restore/${id}`);
      toast.success('Copia restaurada correctamente');
      fetchBackups();
    } catch (error: any) {
      console.error('Error restoring backup', error);
      toast.error(error.response?.data?.error || 'No se pudo restaurar la copia');
    }
  };

  const deleteBackup = async (id: string) => {
    if (!await confirmDialog('Eliminar esta copia?')) return;
    try {
      await axios.delete(`/api/backup/${id}`);
      toast.success('Copia eliminada');
      fetchBackups();
    } catch (error: any) {
      console.error('Error deleting backup', error);
      toast.error(error.response?.data?.error || 'No se pudo eliminar la copia');
    }
  };

  const restoreFromUpload = async () => {
    const file = fileInputRef.current?.files?.[0];
    if (!file) {
      toast.error('Selecciona un archivo .sqlite, .sql o .gz');
      return;
    }
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post('/api/backup/restore-upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Base restaurada desde archivo');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      fetchBackups();
    } catch (error: any) {
      console.error('Error restoring uploaded backup', error);
      toast.error(error.response?.data?.error || 'No se pudo restaurar la base');
    }
  };

  const saveConfig = async () => {
    try {
      setSaving(true);
      const payload = {
        ...config,
        retention_days: Number(config.retention_days || 0),
        interval_minutes: Number(config.interval_minutes || 0),
        s3_retention_days: Number(config.s3_retention_days || 0)
      };
      await axios.post('/api/backup/config', payload);
      toast.success('Configuración guardada');
    } catch (error: any) {
      console.error('Error saving backup config', error);
      toast.error(error.response?.data?.error || 'No se pudo guardar la configuracion');
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="space-y-4">
        <h1 className="text-3xl font-semibold text-foreground">Respaldos</h1>
        <p className="text-sm text-muted-foreground">No tenes permisos para acceder a esta seccion.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-foreground">Respaldos</h1>
          <p className="text-sm text-muted-foreground">Gestiona copias de seguridad de la base de datos.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchBackups}>
            Actualizar lista
          </Button>
          <Button onClick={createManualBackup}>Crear copia</Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Copia manual</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-sm text-muted-foreground">Cargando respaldos...</div>
          ) : backups.length === 0 ? (
            <div className="text-sm text-muted-foreground">No hay copias disponibles.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-muted-foreground">
                    <th className="py-2">Archivo</th>
                    <th className="py-2">Tamano</th>
                    <th className="py-2">Creado</th>
                    <th className="py-2">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {backups.map((item) => (
                    <tr key={item.id} className="border-t">
                      <td className="py-2 pr-2">{item.filename}</td>
                      <td className="py-2 pr-2 whitespace-nowrap">{item.size_human || item.size}</td>
                      <td className="py-2 pr-2 whitespace-nowrap">
                        {item.created_at ? new Date(item.created_at).toLocaleString('es-AR') : '-'}
                      </td>
                      <td className="py-2 space-x-2">
                        <Button variant="outline" size="sm" onClick={() => downloadBackup(item.id)}>
                          Descargar
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => restoreBackup(item.id)}>
                          Restaurar
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => deleteBackup(item.id)}>
                          Borrar
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Programacion automatica</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <Label className="text-sm">Habilitar respaldo automatico</Label>
            <Switch
              checked={config.enabled}
              onCheckedChange={(checked) => setConfig((prev) => ({ ...prev, enabled: checked }))}
            />
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Carpeta destino (servidor)</Label>
              <Input
                value={config.destination_path}
                onChange={(e) => setConfig((prev) => ({ ...prev, destination_path: e.target.value }))}
                placeholder="server/backups"
              />
            </div>
            <div className="space-y-2">
              <Label>Intervalo (min.)</Label>
              <Input
                type="number"
                value={config.interval_minutes}
                onChange={(e) => setConfig((prev) => ({ ...prev, interval_minutes: Number(e.target.value) }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Dias de retencion</Label>
              <Input
                type="number"
                value={config.retention_days}
                onChange={(e) => setConfig((prev) => ({ ...prev, retention_days: Number(e.target.value) }))}
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={config.use_cron}
                onCheckedChange={(checked) => setConfig((prev) => ({ ...prev, use_cron: checked }))}
              />
              <Label>Usar expresion CRON</Label>
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Expresion CRON (opcional)</Label>
              <Input
                value={config.cron_expression}
                onChange={(e) => setConfig((prev) => ({ ...prev, cron_expression: e.target.value }))}
                placeholder="0 3 * * *"
              />
              <p className="text-xs text-muted-foreground">
                Recomendado: 20:00 GMT-3 → usar `0 20 * * *` y timezone America/Argentina/Buenos_Aires.
              </p>
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Zona horaria</Label>
              <Input
                value={config.timezone}
                onChange={(e) => setConfig((prev) => ({ ...prev, timezone: e.target.value }))}
                placeholder="America/Argentina/Buenos_Aires"
              />
            </div>
          </div>
          <div className="flex justify-end">
            <Button onClick={saveConfig} disabled={saving}>
              {saving ? 'Guardando...' : 'Guardar configuracion'}
            </Button>
          </div>
          <div className="text-xs text-muted-foreground">
            {config.last_run ? `Ultimo backup: ${new Date(config.last_run).toLocaleString('es-AR')}` : 'Ultimo backup: -'}
            {config.next_run ? ` · Proximo: ${new Date(config.next_run).toLocaleString('es-AR')}` : ''}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Alertas</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2 md:col-span-2">
            <Label>Usuario destinatario</Label>
            <select
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={config.alert_user_id}
              onChange={(e) => setConfig((prev) => ({ ...prev, alert_user_id: e.target.value }))}
            >
              <option value="">Seleccionar usuario</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name} {u.email ? `(${u.email})` : ''}
                </option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground">
              Se usará el email y teléfono del usuario para enviar alertas.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Respaldo en S3</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <Label className="text-sm">Habilitar subida a S3</Label>
            <Switch
              checked={config.s3_enabled}
              onCheckedChange={(checked) => setConfig((prev) => ({ ...prev, s3_enabled: checked }))}
            />
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Region</Label>
              <Input
                value={config.s3_region}
                onChange={(e) => setConfig((prev) => ({ ...prev, s3_region: e.target.value }))}
                placeholder="us-east-1"
              />
            </div>
            <div className="space-y-2">
              <Label>Bucket</Label>
              <Input
                value={config.s3_bucket}
                onChange={(e) => setConfig((prev) => ({ ...prev, s3_bucket: e.target.value }))}
                placeholder="mi-bucket-backups"
              />
            </div>
            <div className="space-y-2">
              <Label>Prefijo (opcional)</Label>
              <Input
                value={config.s3_prefix}
                onChange={(e) => setConfig((prev) => ({ ...prev, s3_prefix: e.target.value }))}
                placeholder="backups/rocam"
              />
            </div>
            <div className="space-y-2">
              <Label>Retencion S3 (dias)</Label>
              <Input
                type="number"
                value={config.s3_retention_days}
                onChange={(e) => setConfig((prev) => ({ ...prev, s3_retention_days: Number(e.target.value) }))}
              />
            </div>
            <div className="space-y-2">
              <Label>AWS Access Key ID</Label>
              <Input
                value={config.s3_access_key_id}
                onChange={(e) => setConfig((prev) => ({ ...prev, s3_access_key_id: e.target.value }))}
                placeholder="AKIA..."
              />
            </div>
            <div className="space-y-2">
              <Label>AWS Secret Access Key</Label>
              <Input
                type="password"
                value={config.s3_secret_access_key}
                onChange={(e) => setConfig((prev) => ({ ...prev, s3_secret_access_key: e.target.value }))}
                placeholder="********"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Restaurar desde archivo</CardTitle>
        </CardHeader>
      <CardContent className="space-y-4">
          <Input ref={fileInputRef} type="file" accept=".sqlite,.sql,.gz" />
          <Button variant="outline" onClick={restoreFromUpload}>
            Restaurar desde archivo
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default Backups;
